// Unexpected errors in the browser go to the server, which alerts the site owner on
// Discord (see notifyError in notify.js). At most a few per page, each message once.
const IGNORE = /ResizeObserver loop|^Script error\.?$|Load failed|Failed to fetch|NetworkError|AbortError|cancelled|The operation was aborted/i;
const MAX_PER_PAGE = 5;
const seen = new Set();

export function reportError(error, where = 'page') {
  try {
    const message = String((error && error.message) || error || '').slice(0, 300);
    const stack = String((error && error.stack) || '').slice(0, 1500);
    if (!message || IGNORE.test(message) || seen.has(message) || seen.size >= MAX_PER_PAGE) return;
    // Browser extensions throw on every site; they aren't ours to fix.
    if (/(chrome|moz|safari(-web)?)-extension:/.test(stack)) return;
    seen.add(message);
    fetch('/api/t/error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, stack, where, path: window.location.pathname, width: window.innerWidth }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Reporting never gets in the way of the site.
  }
}

export function watchErrors() {
  window.addEventListener('error', (e) => {
    // Scripts from other sites (chat, analytics) only say "Script error"; skip them.
    if (e.filename && !e.filename.startsWith(window.location.origin)) return;
    reportError(e.error || e.message, 'page');
  });
  window.addEventListener('unhandledrejection', (e) => reportError(e.reason, 'promise'));
}

const RELOAD_KEY = 'mm-reloaded-for-update';
const FRESH_PARAM = 'fresh';

// Removes the marker reloadForUpdate adds, so the address looks normal again.
export function dropFreshMarker() {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(FRESH_PARAM)) return;
    url.searchParams.delete(FRESH_PARAM);
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  } catch {
    // Leaving the marker in is harmless.
  }
}

// A part of the app that loads later (like the article text) can be missing after the
// site is updated while the page is open. Reload once to get the new version.
export function reloadForUpdate(error) {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last > 60000) {
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
      // A one-time marker in the address makes the browser fetch the page fresh instead of
      // reusing the old copy it kept; dropFreshMarker() takes it out again.
      const url = new URL(window.location.href);
      url.searchParams.set(FRESH_PARAM, String(Date.now()));
      window.location.replace(url.toString());
      return;
    }
  } catch {
    // No session storage: fall through and report it.
  }
  reportError(error, 'loading part of the app');
}
